<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, watch } from 'vue'
import { useNotification } from "@kyvg/vue3-notification";
import { useStore } from 'vuex'
import FormButton from '../Atoms/FormButton.vue'
import FormInput from '../Atoms/FormInput.vue'
import MediaflowService from '@/services/MediaflowService';
import StreamSourceDropdown from "@/components/ui/StreamSourceDropdown.vue"
import FormSwitch from "@/components/Atoms/FormSwitch.vue"
import FormUpload from '../Atoms/FormUpload.vue';
import AlertModal from "@/components/modals/AlertModal.vue";
import Alert from '@/components/Atoms/Alert.vue';
import ModalBox from './ModalBox.vue'
import { graphicTemplates, normalizeWebPageUrl } from '@/components/Mediaflow/mediaflowMeta'
import MediaflowTypePicker from '@/components/Mediaflow/MediaflowTypePicker.vue'

const props = defineProps({
  mediaflowId: {
    type: String,
    default: '',
    required: true
  },
  /**
   * Flow the source is added to. In `overlay` mode it is only used for image
   * uploads (the images endpoint is flow-scoped), so pass the Preview scene.
   */
  flowId: {
    type: String,
    default: '',
    required: true
  },
  /** `flow` adds a source to a flow; `overlay` creates a production-level graphic. */
  mode: {
    type: String,
    default: 'flow',
    validator: (v) => ['flow', 'overlay'].includes(v),
  },
  canvas: {
    type: Object,
    default: () => ({ width: 1920, height: 1080 }),
  },
  /** Existing graphics rows, for the row picker (overlay mode). */
  rows: {
    type: Array,
    default: () => [],
  },
  /** The scene is drafted (on air, Edit on air off): hand the source back instead of creating it. */
  stage: {
    type: Boolean,
    default: false,
  },
  /** The production's output livestream: it can't also be a source (it would feed itself). */
  destinationId: {
    type: String,
    default: '',
  },
})

// Two instances (source + graphic) can sit on one page: ids must not collide.
const uid = Math.random().toString(36).slice(2, 8)
const uploadInputId = `embed-watermark-input-${uid}`

const isOverlay = computed(() => props.mode === 'overlay')
const templates = computed(() => graphicTemplates(props.canvas))
// The Graphics dock opens straight into this sheet: the quick templates are
// the first five plus Custom (no preset position), and the last pick is remembered.
const quickTemplates = computed(() => templates.value.slice(0, 5))
const LAST_TEMPLATE_KEY = 'castr-mf-last-graphic-template'
// One-row template cards need short names; the full label still names the graphic.
const SHORT_TEMPLATE_LABELS = { 'logo-bug': 'Logo', 'lower-third': 'Lower 3rd', ticker: 'Ticker', scoreboard: 'Score', 'pip-video': 'PiP' }
const selectedTemplate = ref(null)
const overlayTransition = ref('Fade')
// The name follows the template until the operator types their own.
const autoName = ref('')
const overlayRow = ref('Graphics')
const rowOptions = computed(() => {
  const names = new Set([...(props.rows || []), overlayRow.value].filter(Boolean))
  templates.value.forEach((t) => names.add(t.row))
  return Array.from(names)
})

const store = useStore()
const { notify } = useNotification()

const isVisible = ref(false)
const creating = ref(false)
const error = ref(null)
const selectedMediaType = ref({
  label: 'Livestream',
  value: 'live'
})
const streamSourceDropdownVisible = ref(false)
const streamSourceDropdownRef = ref(null)

const liveRestartPlayback = ref(false)
const selectedStream = ref(null)

const loopVOD = ref(false)
const restartPlaybackVOD = ref(false)
const showEmptyOnEndVOD = ref(false)

const maxPosterUploadMB = ref(2)
const embedWatermarkTemp = ref(null)
const embedWatermarkTempImageId = ref(null)
const embedWatermarkTempUrl = ref(null)

const sourceUrl = ref(null)
const sourceUrlCheck = computed(() => normalizeWebPageUrl(sourceUrl.value))
const DESTINATION_SOURCE_HINT = "This livestream is this production's output, so it can't be a source."
const isDestinationStream = (stream) => !!props.destinationId && stream?._id === props.destinationId

const features = ref({
  embedWatermark: {
    error: false,
    enabled: false,
    value: null,
    valueType: 'string'
  }
})

const featureProcessing = ref({
  embedWatermark: false,
})

const allMediaTypes = [
  { label: 'Livestream', value: 'live', hint: 'A camera or stream in your account' },
  { label: 'VOD', value: 'vod', hint: 'A video from your library' },
  { label: 'Image', value: 'image', hint: 'Logo, lower third or still card' },
  { label: 'URL', value: 'url', hint: 'Any web page, e.g. a scoreboard' },
]
// Graphics are stills, pages or clips drawn over the program — never a camera.
const mediaTypes = computed(() => (isOverlay.value
  ? [
    { label: 'Image', value: 'image', hint: 'Logo, lower third, L-band frame' },
    { label: 'URL', value: 'url', hint: 'Live scoreboard, ticker, HTML graphic' },
    { label: 'VOD', value: 'vod', hint: 'A clip from your library' },
  ]
  : allMediaTypes))
const media = ref({
  name: '',
  type: allMediaTypes[0].value,
})

const applyTemplate = (template) => {
  selectedTemplate.value = template
  try { localStorage.setItem(LAST_TEMPLATE_KEY, template?.key || 'custom') } catch (e) { /* private mode */ }
  if (!media.value.name || media.value.name === autoName.value) {
    media.value.name = template ? template.label : ''
    autoName.value = media.value.name
  }
  if (!template) return
  overlayRow.value = template.row
  // The template decides the type (its primary one); the operator can still switch.
  const next = mediaTypes.value.find((t) => t.value === template.types[0])
  if (next && next.value !== selectedMediaType.value.value) onMediaTypecSelect(next)
}

const createMediaValidation = computed(() => {
  if (media.value.name.length === 0) {
    return true
  }

  if (selectedMediaType.value.value === 'live' || selectedMediaType.value.value === 'vod') {
    return !selectedStream.value
  }

  if (selectedMediaType.value.value === 'image') {
    return !embedWatermarkTempImageId.value || !embedWatermarkTempUrl.value
  }

  if (selectedMediaType.value.value === 'url') {
    return !sourceUrlCheck.value.url
  }

  return false
})

const emit = defineEmits(['media-added', 'overlay-added', 'media-staged'])

const imageUrl = computed(() => {
  if (embedWatermarkTemp.value) {
    return embedWatermarkTemp.value
  }

  return null
})

const onEmbedPosterPreview = (file, name) => {
  const selector = `#${uploadInputId}`;
  const imageInput = document.querySelector(selector);
  const imageFile = imageInput.files[0];

  if (!imageFile) return;

  if (imageFile.type.indexOf('image/') === -1) {
    showModal(`embed-image-mime-type-${uid}`);
    imageInput.value = null;
    return;
  }

  let maxSize = 2
  maxPosterUploadMB.value = maxSize;

  maxSize *= 1024 * 1024;

  if (imageFile.size > maxSize) {
    showModal(`embed-image-size-exceed-${uid}`);
    imageInput.value = null;
    return;
  }

  const validImageTypes = ['png', 'jpeg', 'gif', 'avif']
  const uploadedImageFileType = imageFile.type.replace('image/', '');
  if (
    validImageTypes &&
    validImageTypes.indexOf(uploadedImageFileType) === -1
  ) {
    showModal(`embed-image-mime-type-${uid}`);
    imageInput.value = null;
    return;
  }

  imageReader(imageFile, base64 => {
    embedWatermarkTemp.value = base64;
  })
}

const saveEmbedPoster = async (type) => {
  const featureProp = 'embedWatermark'
  features.value[featureProp].error = false;
  const selector = `#${uploadInputId}`
  const imageInput = document.querySelector(selector);

  const imageFile = imageInput.files[0];
  if (!imageInput.value || !imageFile) {
    features.value[featureProp].error = 'please pick an image file';
    return;
  }

  featureProcessing.value[featureProp] = true;
  const fdata = new FormData();
  fdata.append('file', imageFile);
  const res = await MediaflowService.uploadFlowImage(
    props.mediaflowId,
    props.flowId,
    fdata
  );
  if (res.success) {
    embedWatermarkTempImageId.value = res.imageId
    embedWatermarkTempUrl.value = res.imageUrl

    notify({
      group: 'success',
      text: `image uploaded successfully.`
    });
    imageInput.value = ''
  } else {
    features.value[featureProp].error = 'Unable to upload the image. Please try again later.';
  }
  featureProcessing.value[featureProp] = false;
}

const cancelUpload = async (type) => {
  const selector = `#${uploadInputId}`
  const previewFlag = embedWatermarkTemp.value
  if (previewFlag) {
    embedWatermarkTemp.value = null;
    document.querySelector(selector).value = null;
  }
  removeImage(true);
}

const removeImage = async (toast = false) => {
  if (!embedWatermarkTempImageId.value) {
    return
  }

  try {
    await MediaflowService.deleteFlowImage(
      props.mediaflowId,
      props.flowId,
      embedWatermarkTempImageId.value
    )
    embedWatermarkTempImageId.value = null
    embedWatermarkTempUrl.value = null

    if (toast) {
      notify({ group: 'success', text: 'Image media removed' })
    }
    const settingsProp = 'embedWatermark'
    features.value[settingsProp].value = null;
  } catch (e) {
    console.log(e);
  }
}

function imageReader(file, cb) {
  if (!file) {
    return;
  }
  var reader = new FileReader();
  reader.onload = function () {
    if (cb) {
      cb(reader.result);
    }
  };
  reader.readAsDataURL(file);
}

const closeModal = () => {
  isVisible.value = false
}
// Kept for API compatibility with the parent (`addFlowMediaModalRef.toggleSidebar()`).
// Overlay mode accepts `{ template, row }` to preselect a quick template / row.
const toggleSidebar = (options = {}) => {
  isVisible.value = !isVisible.value
  if (isVisible.value && isOverlay.value) {
    onReset()
    if (options.row) overlayRow.value = options.row
    let template = options.template || null
    if (!template) {
      let last = null
      try { last = localStorage.getItem(LAST_TEMPLATE_KEY) } catch (e) { /* private mode */ }
      template = last === 'custom' ? null : (quickTemplates.value.find((t) => t.key === last) || quickTemplates.value[0])
    }
    applyTemplate(template)
  }
}
const onReset = () => {
  media.value = { name: '', type: mediaTypes.value[0].value }
  error.value = null
  selectedStream.value = null
  embedWatermarkTemp.value = null

  embedWatermarkTempImageId.value = null
  embedWatermarkTempUrl.value = null

  sourceUrl.value = null
  selectedTemplate.value = null
  autoName.value = ''
  overlayTransition.value = 'Fade'
  overlayRow.value = 'Graphics'

  selectedMediaType.value = mediaTypes.value[0]

  streamSourceDropdownRef.value?.closeDropdown?.()
  streamSourceDropdownVisible.value = false
  creating.value = false

  removeImage(false)
}

const onMediaTypecSelect = (typ) => {
  selectedMediaType.value = typ
  selectedStream.value = null
  error.value = null
}

const onSourceDropdownShow = () => {
  streamSourceDropdownVisible.value = true
}

const onSourceDropdownHide = () => {
  streamSourceDropdownVisible.value = false
}

const onSourceStreamSelected = async (stream) => {
  if (selectedMediaType.value.value === 'live' && isDestinationStream(stream)) {
    error.value = DESTINATION_SOURCE_HINT
    return
  }
  error.value = null
  selectedStream.value = stream
}

const createMedia = async () => {
  if (createMediaValidation.value || creating.value) return
  creating.value = true
  try {
    const payload = {
      name: media.value.name,
      type: selectedMediaType.value.value,
    }

    if (payload.type === 'live') {
      if (isDestinationStream(selectedStream.value)) {
        error.value = DESTINATION_SOURCE_HINT
        creating.value = false
        return
      }
      payload.stream = selectedStream.value._id
      // payload.liveRestartPlayback = liveRestartPlayback.value
    }

    if (payload.type === 'vod') {
      payload.stream = selectedStream.value.file._id
      payload.key = selectedStream.value.folder.key
      payload.file = selectedStream.value.file.id
      payload.fileNmae = selectedStream.value.file.fileNmae
      payload.loop = loopVOD.value
      payload.restartPlayback = restartPlaybackVOD.value
      payload.showEmptyOnEnd = showEmptyOnEndVOD.value

      if (selectedStream.value?.file?.downloadUrl) {
        //live to vod
        payload.downloadUrl = selectedStream.value.file.downloadUrl
        payload.refId = selectedStream.value.file.refId
      }
    }

    if (payload.type === 'image') {
      payload.imageId = embedWatermarkTempImageId.value
      payload.imageUrl = embedWatermarkTempUrl.value
    }

    if (payload.type === 'url') {
      payload.sourceUrl = sourceUrlCheck.value.url
    }

    if (isOverlay.value) {
      // A graphic is production-level: it lands above every scene, placed
      // where the quick template says (or full-frame when none was picked).
      const mediaPayload = { ...payload, show: true }
      if (selectedTemplate.value?.transform) mediaPayload.transform = { ...selectedTemplate.value.transform }
      const overlay = await MediaflowService.createOverlay(props.mediaflowId, {
        name: payload.name,
        row: overlayRow.value || 'Graphics',
        media: mediaPayload,
        transition: overlayTransition.value === 'Cut' ? { type: 'Cut', duration: 0 } : { type: 'Fade', duration: 300 },
      })
      emit('overlay-added', overlay)
      notify({ group: 'success', text: 'Graphic added' })
      embedWatermarkTempImageId.value = null
      embedWatermarkTempUrl.value = null
      closeModal()
      creating.value = false
      return
    }

    if (props.stage) {
      emit('media-staged', payload)
      // The upload belongs to the staged source now (deleted if the draft is discarded).
      embedWatermarkTempImageId.value = null
      embedWatermarkTempUrl.value = null
      closeModal()
      creating.value = false
      return
    }

    const newMedia = await MediaflowService.createFlowMedia(props.mediaflowId, props.flowId, payload)
    emit('media-added', newMedia)
    notify({ group: 'success', text: 'Source added' })
    // The upload now belongs to the new source: forget the temp id so the
    // close-time cleanup does not delete it.
    embedWatermarkTempImageId.value = null
    embedWatermarkTempUrl.value = null
    closeModal()
  } catch (err) {
    error.value = err.message
  }
  creating.value = false
}

const onSidebarClosed = () => {
  onReset()
}
// Any close path (Cancel, X, backdrop, Escape, success) cleans up like the old drawer did.
watch(isVisible, (open) => { if (!open) onReset() })

defineExpose({ toggleSidebar })
</script>

<template>
  <!-- The Livestream / Video pickers drop down inside the body: it must not clip them on desktop. -->
  <ModalBox v-model="isVisible" :title="isOverlay ? 'Add graphic' : 'Add source'" size="sm" placement="center" mobile-placement="bottom" header-class="!px-5 !pt-5 !pb-3" body-classes="lg:!overflow-visible">
      <div class="px-5" :data-mode="mode">
        <Alert v-if="error" type="error" class="mb-3">{{ error }}</Alert>

        <!-- Template (graphics only): sets the type and where it lands -->
        <div v-if="isOverlay" class="mb-3">
          <span class="block text-tiny font-medium text-surface-text-1 mb-1.5">Template</span>
          <div class="grid grid-cols-3 gap-1.5 sm:grid-cols-6" role="radiogroup" aria-label="Graphic template">
            <button
              v-for="t in [...quickTemplates, null]"
              :key="t ? t.key : 'custom'"
              type="button"
              role="radio"
              :aria-checked="(selectedTemplate?.key || null) === (t?.key || null)"
              class="flex min-w-0 flex-col items-center gap-1 rounded-lg px-1 py-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
              :class="(selectedTemplate?.key || null) === (t?.key || null) ? 'bg-main-transparent ring-1 ring-main' : 'bg-surface-3 hover:bg-surface-4'"
              :title="t ? t.hint : 'Image, web page or clip, any size'"
              :data-testid="`add-graphic-template-${t ? t.key : 'custom'}`"
              @click="applyTemplate(t)"
            >
              <span v-if="t" class="relative block h-6 w-10 overflow-hidden rounded-[3px] bg-surface-5" aria-hidden="true">
                <span class="absolute rounded-[1px] bg-gold" :style="{ left: `${(t.transform.positionX / canvas.width) * 100}%`, top: `${(t.transform.positionY / canvas.height) * 100}%`, width: `${t.transform.scaleX}%`, height: `${t.transform.scaleY}%` }" />
              </span>
              <span v-else class="block h-6 w-10 rounded-[3px] border border-dashed border-surface-6" aria-hidden="true" />
              <span class="block w-full truncate text-center text-[0.7rem] font-medium leading-tight text-surface-text-2">{{ t ? (SHORT_TEMPLATE_LABELS[t.key] || t.label) : 'Custom' }}</span>
            </button>
          </div>
          <p class="m-0 mt-1.5 text-xs text-surface-8">{{ selectedTemplate ? `${selectedTemplate.hint}.` : 'Full frame.' }} Fine-tune later on Preview.</p>
        </div>

        <MediaflowTypePicker class="mb-3" :options="mediaTypes" :model-value="selectedMediaType.value" :label="isOverlay ? 'Graphic type' : 'Source type'" @select="onMediaTypecSelect" />

        <div class="mb-3">
          <FormInput v-model="media.name" label="Name" placeholder="e.g. Main camera, Sponsor reel" />
        </div>

        <div v-if="selectedMediaType.value === 'live'" class="mb-3">
          <label class="block text-tiny font-medium text-surface-text-1 mb-2">Livestream</label>
          <stream-source-dropdown ref="streamSourceDropdownRef" @on-show="onSourceDropdownShow"
            @on-hide="onSourceDropdownHide" @on-source-selected="onSourceStreamSelected"
            :selectedStream="selectedStream" :dropRight="false" :onlyLiveSelector="true" :dropdownDisabled="false"
            source="multiview" :refLink="0" :disabled-streams="destinationId || null" :disabled-tooltip="DESTINATION_SOURCE_HINT"
            :max-list-height="300" />
        </div>

        <div v-if="selectedMediaType.value === 'vod'" class="mb-3">
          <label class="block text-tiny font-medium text-surface-text-1 mb-2">Video from your library</label>
          <stream-source-dropdown ref="streamSourceDropdownRef" @on-show="onSourceDropdownShow"
            @on-hide="onSourceDropdownHide" @on-source-selected="onSourceStreamSelected"
            :selectedStream="selectedStream" :dropRight="false" source="mediaflow" :showPlaylist="false"
            :hideLive="true" :refLink="0" :max-list-height="300" />
        </div>

        <div v-if="selectedMediaType.value === 'vod'" class="mb-3 flex flex-col gap-3 rounded-lg bg-surface-3 p-3">
          <span class="text-[0.65rem] font-semibold uppercase tracking-wider text-surface-6">Playback</span>
          <FormSwitch size="md" label="Loop" labelPosition="right" v-model="loopVOD" />
          <FormSwitch size="md" label="Restart from the beginning when this scene goes on air" labelPosition="right"
            v-model="restartPlaybackVOD" />
          <FormSwitch size="md" label="Show nothing when playback ends" labelPosition="right"
            v-model="showEmptyOnEndVOD" />
        </div>

        <div v-if="selectedMediaType.value === 'image'" class="mb-3">
          <label class="block text-tiny font-medium text-surface-text-1 mb-2">Image (PNG, JPG, GIF or AVIF, up to {{ maxPosterUploadMB }} MB)</label>
          <div class="flex justify-between items-center gap-x-2">
            <div class="flex">
              <img v-if="imageUrl" width="100px" :src="imageUrl" class="w-full size-full md:w-[100px]"  />
              <p v-else class="text-tiny text-surface-6">No file chosen</p>
            </div>

            <div class="flex">
              <FormUpload :class="{ hidden: embedWatermarkTemp }" name="watermark" :id="uploadInputId"
                placeholder="No file chosen" type="secondary" :label="embedWatermarkTemp ? 'Change' : 'Select image'"
                :on-file-change="onEmbedPosterPreview" :accept-type="'image/*'" />
              <FormButton v-if="embedWatermarkTemp" :class="{ hidden: embedWatermarkTempImageId }"
                :disabled="featureProcessing.embedWatermark" @click="saveEmbedPoster('watermark')">
                {{ featureProcessing.embedWatermark ? 'Saving' : 'Save' }}
              </FormButton>
              <FormButton v-if="embedWatermarkTemp || features.embedWatermark.value" class="ml-2"
                :disabled="featureProcessing.embedWatermark" type="secondary" @click="cancelUpload('watermark')">
                <span v-if="embedWatermarkTemp">Cancel</span>
                <span v-else>Remove</span>
              </FormButton>
            </div>
          </div>
          <Alert v-show="features.embedWatermark.error" type="error" class="mt-2">
            {{ features.embedWatermark.error }}
          </Alert>
        </div>

        <div v-if="selectedMediaType.value === 'url'" class="mb-3">
          <FormInput id="add-source-url" v-model="sourceUrl" label="Web page URL" placeholder="https://…" :is-error="!!sourceUrlCheck.error" :helper="sourceUrlCheck.error || 'The page is rendered full-size on the canvas.'" />
        </div>

        <!-- Graphics: how it comes on / off air, and its group -->
        <div v-if="isOverlay" class="mb-3 mt-5">
          <span class="block text-tiny font-medium text-surface-text-1 mb-2">Transition on / off air</span>
          <div class="grid grid-cols-2 gap-0.5 rounded-lg bg-surface-1 p-0.5 ring-1 ring-surface-3" role="radiogroup" aria-label="Transition">
            <button
              v-for="t in ['Fade', 'Cut']"
              :key="t"
              type="button"
              role="radio"
              :aria-checked="overlayTransition === t"
              class="h-7 rounded-md text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-main"
              :class="overlayTransition === t ? 'bg-surface-5 text-white ring-1 ring-surface-6' : 'text-surface-8 hover:text-white'"
              :data-testid="`add-graphic-transition-${t.toLowerCase()}`"
              @click="overlayTransition = t"
            >
              {{ t === 'Fade' ? 'Fade · 300 ms' : 'Cut' }}
            </button>
          </div>
        </div>
        <template v-if="isOverlay">
          <div class="mb-3">
            <label class="block text-tiny font-medium text-surface-text-1 mb-2" for="add-graphic-row">Group <span class="text-surface-7">(optional)</span></label>
            <input
              id="add-graphic-row"
              v-model="overlayRow"
              list="add-graphic-rows"
              class="h-8 w-full rounded bg-surface-3 px-3 text-tiny text-surface-text-2 ring-1 ring-transparent focus:bg-surface-4 focus:outline-none focus:ring-main"
              placeholder="Graphics"
              maxlength="60"
              data-testid="add-graphic-row"
            >
            <datalist id="add-graphic-rows">
              <option v-for="r in rowOptions" :key="r" :value="r" />
            </datalist>
          </div>
        </template>
      </div>

    <template #footer>
      <div class="flex flex-col-reverse gap-2 px-5 pb-5 pt-2 sm:flex-row sm:justify-end">
        <FormButton type="secondary" size="md" class="w-full justify-center sm:w-auto" @click="closeModal">Cancel</FormButton>
        <FormButton type="main" size="md" class="w-full justify-center sm:w-auto" :disabled="createMediaValidation" :is-loading="creating" data-testid="add-source-submit" @click="createMedia">{{ isOverlay ? 'Add graphic' : 'Add source' }}</FormButton>
      </div>
    </template>
  </ModalBox>

  <alert-modal :message="'Uploaded image too large. Image must not exceed ' + maxPosterUploadMB + 'MB'"
    :modal-id="`embed-image-size-exceed-${uid}`" />
  <alert-modal :modal-id="`embed-image-mime-type-${uid}`" message="Only PNG/JPG/GIF/AVIF image files are allowed" />
</template>